import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type PhotoSlot = "front" | "left" | "right";
type ProductType = "skin" | "hair";

interface UserData {
  fullName: string;
  phone: string;
  email: string;
}

interface ReportCounterScreenProps {
  onComplete: () => void;
  onViewReport: () => void;
  duration?: number;
  email: string;
  onHome: () => void;
  onReset: () => void;
  userData?: UserData;
  photos?: Record<PhotoSlot, string>;
  hairPhoto?: string;
  hairQuestionnaire?: any;
  productType?: ProductType;
  tenantId?: string;
  locale?: string;
  compressImage?: (dataUrl: string) => Promise<string>;
}

export const ReportCounterScreen = ({ 
  onComplete, 
  onViewReport,
  duration = 30,
  email,
  onHome,
  onReset,
  userData,
  photos,
  hairPhoto,
  hairQuestionnaire,
  productType,
  tenantId,
  locale,
  compressImage
}: ReportCounterScreenProps) => {
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(duration);
  const [isComplete, setIsComplete] = useState(false);
  const [analysisStarted, setAnalysisStarted] = useState(false);

  // Start analysis in background
  useEffect(() => {
    if (!analysisStarted && userData && tenantId && locale && compressImage) {
      setAnalysisStarted(true);

      const normalizeHairQuestionnaire = (q: any) => {
        if (!q || typeof q !== "object") return q;
        const toNumberIfPossible = (v: any) => {
          const n = typeof v === "number" ? v : Number(v);
          return Number.isFinite(n) ? n : v;
        };

        return {
          ...q,
          age: toNumberIfPossible(q.age),
          sleepHours: toNumberIfPossible(q.sleepHours),
        };
      };
      
      const runAnalysis = async () => {
        try {
          let requestBody: any = {
            mode: productType,
            locale,
            tenantId,
            fullName: userData.fullName,
            phone: userData.phone,
            email: userData.email,
          };

          if (productType === "hair" && hairPhoto) {
            const compressedPhoto = await compressImage(hairPhoto);
            requestBody.photo = compressedPhoto;
            requestBody.questionnaire = normalizeHairQuestionnaire(hairQuestionnaire);
          } else if (productType === "skin" && photos) {
            const [compressedFront, compressedLeft, compressedRight] = await Promise.all([
              compressImage(photos.front),
              compressImage(photos.left),
              compressImage(photos.right),
            ]);

            requestBody.photos = {
              front: compressedFront,
              left: compressedLeft,
              right: compressedRight,
            };
          }

          console.log("Starting analysis in background...");
          const { data, error } = await supabase.functions.invoke("analyze-face", {
            body: requestBody,
          });

          if (error) {
            console.error("Analysis error:", error);
            toast.error(error.message || "Analysis failed");
            return;
          }

          if (!data?.success) {
            console.error("Analysis failed:", data);
            toast.error(data?.error || "Analysis failed");
            return;
          }

          console.log("Analysis completed successfully");

          // Notify front desk with lead details (best-effort; should not block user flow)
          const leadPayload = {
            fullName: userData.fullName,
            phone: userData.phone,
            email: userData.email,
            mode: productType === "hair" ? "hair" : "skin",
            tenantId,
          };

          // Send to both frontdesk email and external webhook in parallel
          await Promise.allSettled([
            supabase.functions.invoke("send-lead-to-frontdesk", { body: leadPayload }),
            supabase.functions.invoke("send-lead-to-webhook", { body: leadPayload }),
          ]).then((results) => {
            results.forEach((result, index) => {
              const target = index === 0 ? "frontdesk" : "webhook";
              if (result.status === "rejected") {
                console.warn(`${target} notification failed:`, result.reason);
              }
            });
          });
        } catch (error: any) {
          console.error("Analysis error:", error);
          toast.error(error.message || "Analysis failed");
        }
      };

      runAnalysis();
    }
  }, [analysisStarted, userData, photos, hairPhoto, hairQuestionnaire, productType, tenantId, locale, compressImage]);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsComplete(true);
          return 100;
        }
        return prev + (100 / duration);
      });

      setTimeRemaining(prev => {
        if (prev <= 0) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [duration]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#002E9A] via-[#0A44D6] to-[#002E9A] flex flex-col overflow-hidden max-w-[100vw]">
      {/* Header */}
      <div className="pt-6 pb-4 px-4 text-center flex-shrink-0">
        <h1 className="text-white text-xl font-medium">
          {isComplete ? "Analysis Complete" : "Final Analysis"}
        </h1>
      </div>

      {/* Main Timer Section */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-y-auto overflow-x-hidden">
        {/* Circular Progress */}
        <div className="relative w-48 h-48 sm:w-56 sm:h-56 mb-6 flex-shrink-0">
          {/* Background circle */}
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 224 224">
            <circle
              cx="112"
              cy="112"
              r="100"
              stroke="rgba(255, 255, 255, 0.1)"
              strokeWidth="10"
              fill="none"
            />
            {/* Progress circle */}
            <circle
              cx="112"
              cy="112"
              r="100"
              stroke="#F4C86A"
              strokeWidth="10"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 100}`}
              strokeDashoffset={`${2 * Math.PI * 100 * (1 - progress / 100)}`}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-linear"
              style={{
                filter: "drop-shadow(0 0 8px rgba(244, 200, 106, 0.4))"
              }}
            />
          </svg>
          
          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {isComplete ? (
              <div className="bg-[#F4C86A] rounded-full p-3 mb-2 animate-scale-in">
                <Check className="w-10 h-10 text-[#002E9A]" />
              </div>
            ) : (
              <>
                <div className="text-4xl font-semibold text-white mb-2">
                  {formatTime(timeRemaining)}
                </div>
                <div className="text-white/80 text-sm font-medium">
                  {Math.round(progress)}% Complete
                </div>
              </>
            )}
          </div>

          {/* Glow effect */}
          <div 
            className="absolute inset-0 rounded-full blur-2xl opacity-30 transition-opacity duration-1000"
            style={{
              background: `radial-gradient(circle, #F4C86A ${progress}%, transparent 70%)`,
            }}
          />
        </div>

        {/* Status text */}
        <div className="text-center mb-6 flex-shrink-0">
          <p className="text-white/90 text-sm mb-2">
            {isComplete
              ? `Your personalized ${productType === "hair" ? "hair" : "skin"} report is ready`
              : `Finalizing your personalized ${productType === "hair" ? "hair" : "skin"} report...`
            }
          </p>
          {!isComplete && (
            <div className="flex items-center justify-center gap-1">
              <div className="w-2 h-2 bg-[#F4C86A] rounded-full animate-pulse" />
              <div className="w-2 h-2 bg-[#F4C86A] rounded-full animate-pulse delay-100" />
              <div className="w-2 h-2 bg-[#F4C86A] rounded-full animate-pulse delay-200" />
            </div>
          )}
        </div>

        {/* Result Summary Card */}
        {isComplete && (
          <div className="space-y-3 max-w-sm w-full flex-shrink-0">
            <div className="bg-white rounded-2xl p-4 shadow-xl border border-gray-100 space-y-3 animate-fade-in">
              <div className="text-center pb-3 border-b border-gray-100">
                <h2 className="text-[#002E9A] text-base font-semibold mb-1">Report Generated Successfully</h2>
                <p className="text-gray-600 text-xs">Your personalized analysis is ready</p>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Analysis Status</span>
                  <span className="text-[#002E9A] font-semibold">Complete</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">AI Confidence</span>
                  <span className="text-[#F4C86A] font-semibold">97%</span>
                </div>
                <div className="flex flex-col items-start pt-1">
                  <span className="text-gray-600 font-medium mb-1">Report Sent To:</span>
                  <span className="text-[#002E9A] font-semibold break-all text-sm">{email}</span>
                  <span className="text-gray-400 text-xs mt-1">Check your inbox and spam folder</span>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-3 space-y-1 animate-fade-in">
              <p className="text-white text-xs font-medium">Have questions?</p>
              <p className="text-white/80 text-xs">frontdesk@savoyclinic.ae • +971 56 689 2095</p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 animate-fade-in">
              <Button
                onClick={onHome}
                variant="outline"
                className="w-full h-11 bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-xl font-medium text-sm"
              >
                Return Home
              </Button>
              <Button
                onClick={onReset}
                className="w-full h-11 bg-white text-[#002E9A] hover:bg-white/90 rounded-xl font-semibold text-sm"
              >
                Start New Analysis
              </Button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};
